import { useEffect, useMemo, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Button,
  LoadingSkeleton,
  Pagination,
  Select,
  Table,
  TextInput,
  useBreakpoints
} from "@narmi/design_system";
import { DateTime } from "luxon";
import Transaction from "byzantine/src/Transaction";
import { parseAmountInputToCents } from "byzantine/src/dbbl/utils";
import type Account from "byzantine/src/Account";
import type { SettledTransactionsState } from "../types";
import { useAccountsContext } from "../../contexts/AccountContext";
import usePendingTransactions from "../../hooks/usePendingTransactions";
import {
  EmptyTransactionState,
  NoMatchedTransactionFilters
} from "../../RecentTransactionsTable";
import useTransactionPageSize from "./hooks";
import TransactionRow, { TransactionTabletRow } from "./TransactionRow";
import TransactionTypeFilterChip from "./TransactionTypeFilterChip";
import DescriptionFilterChip from "./DescriptionFilterChip";
import AccountFilterChip from "./AccountFilterChip";
import RangeFilterChip from "./RangeFilterChip";
import DateFilterInput from "./DateFilterInput";
import styles from "./RecentTransactionsModule.module.scss";

const MAX_TRANSACTIONS = 100;
const MOBILE_COLLAPSED_SIZE = 5;

type RecentTransactionsModuleProps = {
  replyTime: string;
  settledTransactionsState: SettledTransactionsState;
};

const RecentTransactionsModule = ({
  replyTime,
  settledTransactionsState
}: RecentTransactionsModuleProps) => {
  const { l10n } = useLocalization();
  // Recent transactions only ever cover internal accounts (external accounts
  // don't have a transaction feed here), so the account filter dropdown and
  // pending-transactions lookup should only ever consider internal accounts.
  const { internalAccounts }: { internalAccounts: Account[] } =
    useAccountsContext();
  const { l: isDesktop } = useBreakpoints();
  const { isLoading, settledTransactions } = settledTransactionsState;
  const visibleAccounts = useMemo(
    () => internalAccounts.filter((a) => !a.hidden),
    [internalAccounts]
  );

  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>(
    []
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [moneyFilter, setMoneyFilter] = useState<"in" | "out" | null>(null);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(
    new Set()
  );
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  usePendingTransactions({
    accounts: internalAccounts,
    setPendingTransactions,
    excludeHiddenAccounts: true
  });

  useEffect(() => {
    const merged = Transaction.sortByPostedDate([
      ...pendingTransactions,
      ...settledTransactions
    ]).slice(0, MAX_TRANSACTIONS);
    setAllTransactions(merged);
    setCurrentPage(1);
  }, [
    JSON.stringify(pendingTransactions),
    JSON.stringify(settledTransactions)
  ]);

  const amountFromCents = useMemo(
    () => parseAmountInputToCents(amountFrom),
    [amountFrom]
  );
  const amountToCents = useMemo(
    () => parseAmountInputToCents(amountTo),
    [amountTo]
  );

  const amountInputError = (value: string) =>
    value && parseAmountInputToCents(value) === null
      ? l10n.getString("recent-transactions-amount-filter-error")
      : undefined;

  const filteredTransactions = useMemo(() => {
    let txns = allTransactions;
    if (moneyFilter === "in") txns = txns.filter((t) => t.amount > 0);
    if (moneyFilter === "out") txns = txns.filter((t) => t.amount < 0);
    if (appliedSearch) {
      const lower = appliedSearch.toLowerCase();
      txns = txns.filter((t) =>
        (t.description || "").toLowerCase().includes(lower)
      );
    }
    if (selectedAccountIds.size > 0) {
      txns = txns.filter(
        (t) => t.account?.id && selectedAccountIds.has(t.account.id)
      );
    }
    if (amountFromCents !== null) {
      const min = Math.abs(amountFromCents);
      txns = txns.filter((t) => Math.abs(t.amount) >= min);
    }
    if (amountToCents !== null) {
      const max = Math.abs(amountToCents);
      txns = txns.filter((t) => Math.abs(t.amount) <= max);
    }
    if (dateFrom) {
      const from = DateTime.fromISO(dateFrom).startOf("day");
      txns = txns.filter((t) => {
        const raw = t.posted_date || t.creation_date;
        if (!raw) return false;
        return DateTime.fromFormat(raw, "M/d/yyyy") >= from;
      });
    }
    if (dateTo) {
      const to = DateTime.fromISO(dateTo).endOf("day");
      txns = txns.filter((t) => {
        const raw = t.posted_date || t.creation_date;
        if (!raw) return false;
        return DateTime.fromFormat(raw, "M/d/yyyy") <= to;
      });
    }
    return txns;
  }, [
    allTransactions,
    moneyFilter,
    appliedSearch,
    selectedAccountIds,
    amountFromCents,
    amountToCents,
    dateFrom,
    dateTo
  ]);

  const hasActiveFilters =
    moneyFilter !== null ||
    !!appliedSearch ||
    selectedAccountIds.size > 0 ||
    !!amountFrom ||
    !!amountTo ||
    !!dateFrom ||
    !!dateTo;

  const {
    pageSize,
    options: pageSizeOptions,
    isDisabled: isPageSizeSelectDisabled,
    setPreferredPageSize
  } = useTransactionPageSize(filteredTransactions.length);

  const effectivePageSize = isDesktop ? pageSize : 10;
  const totalPages = Math.max(
    1,
    Math.ceil(filteredTransactions.length / effectivePageSize)
  );
  const pageStart = (currentPage - 1) * effectivePageSize;
  // on mobile (handled via CSS), cap to 5 or 10 depending on expanded state
  const mobilePageSize = mobileExpanded ? 10 : MOBILE_COLLAPSED_SIZE;
  const visibleTransactions = useMemo(
    () => filteredTransactions.slice(pageStart, pageStart + effectivePageSize),
    [filteredTransactions, pageStart, effectivePageSize]
  );
  const mobileVisibleTransactions = useMemo(
    () => filteredTransactions.slice(0, mobilePageSize),
    [filteredTransactions, mobilePageSize]
  );

  return (
    <div className={styles.module}>
      <h4 className={styles.moduleHeader}>
        {l10n.getString("recent-transactions-header")}
      </h4>
      <div className={styles.filterPills}>
        <DescriptionFilterChip
          appliedSearch={appliedSearch}
          onApply={(s) => {
            setAppliedSearch(s);
            setCurrentPage(1);
          }}
        />
        <AccountFilterChip
          accounts={visibleAccounts}
          selectedAccountIds={selectedAccountIds}
          onApply={(ids) => {
            setSelectedAccountIds(ids);
            setCurrentPage(1);
          }}
        />
        <RangeFilterChip
          label={l10n.getString("recent-transactions-filter-amount")}
          valueFrom={amountFrom}
          valueTo={amountTo}
          onApply={(from, to) => {
            setAmountFrom(from);
            setAmountTo(to);
            setCurrentPage(1);
          }}
          validate={(from, to) =>
            !!amountInputError(from) || !!amountInputError(to)
          }
          renderFrom={(value, onChange) => (
            <TextInput
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange(e.target.value)
              }
              error={amountInputError(value)}
              placeholder={l10n.getString(
                "recent-transactions-amount-filter-from"
              )}
            />
          )}
          renderTo={(value, onChange) => (
            <TextInput
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange(e.target.value)
              }
              error={amountInputError(value)}
              placeholder={l10n.getString(
                "recent-transactions-amount-filter-to"
              )}
            />
          )}
        />
        <RangeFilterChip
          label={l10n.getString("recent-transactions-filter-date")}
          valueFrom={dateFrom}
          valueTo={dateTo}
          onApply={(from, to) => {
            setDateFrom(from);
            setDateTo(to);
            setCurrentPage(1);
          }}
          validate={(from, to) =>
            !!(from && to && DateTime.fromISO(from) > DateTime.fromISO(to))
          }
          defaultPendingTo={DateTime.now().toISODate() ?? undefined}
          renderFrom={(value, onChange) => (
            <DateFilterInput
              label={l10n.getString("recent-transactions-date-filter-from")}
              initialValue={value}
              onChange={onChange}
            />
          )}
          renderTo={(value, onChange, hasError, pendingFrom) => (
            <DateFilterInput
              label={l10n.getString("recent-transactions-date-filter-to")}
              initialValue={value}
              minDate={pendingFrom}
              onChange={onChange}
              error={
                hasError
                  ? l10n.getString("recent-transactions-date-filter-error")
                  : undefined
              }
            />
          )}
        />
        <TransactionTypeFilterChip
          value={moneyFilter}
          onApply={(v) => {
            setMoneyFilter(v);
            setCurrentPage(1);
          }}
        />
      </div>
      <div className={styles.tableCard}>
        {isLoading && (
          <LoadingSkeleton isLoading content="paragraph" lines={3} />
        )}
        {!isLoading && filteredTransactions.length === 0 && (
          <div className="padding--x--l">
            {hasActiveFilters ? (
              <NoMatchedTransactionFilters />
            ) : (
              <EmptyTransactionState />
            )}
          </div>
        )}
        {!isLoading && filteredTransactions.length > 0 && (
          <>
            {/* Mobile list (hidden on tablet+) */}
            <div className={styles.mobileList}>
              {mobileVisibleTransactions.map((t) => (
                <TransactionTabletRow
                  key={t.id}
                  transaction={t}
                  replyTime={replyTime}
                />
              ))}
            </div>
            {/* Tablet list (hidden on mobile and desktop) */}
            <div className={styles.tabletList}>
              {visibleTransactions.map((t) => (
                <TransactionTabletRow
                  key={t.id}
                  transaction={t}
                  replyTime={replyTime}
                />
              ))}
            </div>
            <div className={styles.desktopTable}>
              <Table
                colVisibility={["*", "*", "*", "*"]}
                colLayout={{
                  s: "0.1fr 0.6fr 0.4fr auto",
                  m: "0.1fr 0.6fr 0.4fr auto",
                  l: "0.1fr 0.6fr 0.4fr auto"
                }}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>
                      {l10n.getString("transactions-table-header-date")}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {l10n.getString("transactions-table-header-description")}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {l10n.getString("transactions-table-header-account")}
                    </Table.HeaderCell>
                    <Table.HeaderCell textAlign="end">
                      {l10n.getString("transactions-table-header-amount")}
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {visibleTransactions.map((t) => (
                    <TransactionRow
                      key={t.id}
                      transaction={t}
                      replyTime={replyTime}
                    />
                  ))}
                </Table.Body>
              </Table>
            </div>
          </>
        )}
      </div>
      {filteredTransactions.length > MOBILE_COLLAPSED_SIZE && (
        <div className={styles.viewMoreButton}>
          <Button
            kind="plain"
            size="s"
            onClick={() => setMobileExpanded((prev) => !prev)}
          >
            {mobileExpanded
              ? l10n.getString("recent-transactions-view-less")
              : l10n.getString("recent-transactions-view-more")}
          </Button>
        </div>
      )}
      {filteredTransactions.length > 0 && (
        <div className={styles.footer}>
          <div className={styles.footerSpacer} />
          <Pagination
            totalPages={totalPages}
            selectedPage={currentPage}
            onPageChange={(page: number) => setCurrentPage(page)}
          />
          <div className={styles.pageSizeSelector}>
            <Select
              value={String(pageSize)}
              disabled={isPageSizeSelectDisabled}
              onChange={(val: string) => {
                setPreferredPageSize(Number(val));
                setCurrentPage(1);
              }}
            >
              {pageSizeOptions.map((n) => (
                <Select.Item key={n} value={String(n)}>
                  {l10n.getString("recent-transactions-show-rows", {
                    count: n
                  })}
                </Select.Item>
              ))}
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentTransactionsModule;
